// main.tsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from 'react-router-dom';
import App from './App.tsx';
import './index.css';
import { store } from './store';
import { Provider } from 'react-redux';
import { TooltipProvider } from '@/components/ui/tooltip';
import ErrorBoundary from '@/components/ErrorBoundary';
import { ThemeProvider, type ThemeName } from './contexts/ThemeContext.tsx';
import { useAppSelector } from './store/hooks.ts';

// Define enum or type
enum UserType {
  ATTENDEE = 'attendee',
  ORGANIZER = 'organization',
  PROMOTER = 'promoter',
  ADMIN = 'admin',
  SYSTEM = 'system'
}

const getUserRole = (userType?: string): { theme: ThemeName; role: string; permissions: string[] } => {
  switch (userType) {
    case UserType.ATTENDEE:
      return { theme: "purple-pink" as const, role: 'Attendee', permissions: ['view_events'] };

    case UserType.ORGANIZER:
      return { theme: "blue-ocean" as const, role: 'Organizer', permissions: ['create_events', 'manage_events'] };

    case UserType.PROMOTER:
      return { theme: "emerald-green" as const, role: 'Promoter', permissions: ['promote_events'] };

    case UserType.ADMIN:
      return { theme: "orange-sunset" as const, role: 'Admin', permissions: ['full_access'] };

    case UserType.SYSTEM:
      return { theme: "royal-purple" as const, role: 'System', permissions: ['system_operations'] };

    default:
      return { theme: "purple-pink" as const, role: 'Guest', permissions: [] };
  }
};

// Sentry configuration
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;

console.log('🔍 Sentry Configuration Check:');
console.log('Environment:', import.meta.env.MODE);
console.log('Sentry DSN:', sentryDsn ? '✅ Configured' : '❌ Not configured');
console.log('DSN:', sentryDsn);

if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    environment: import.meta.env.MODE || 'development',
    release: `aboutblank@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    debug: true,
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
    beforeSend(event, hint) {
      console.log('📤 Sentry Event:', event);
      console.log('Hint:', hint);
      if (import.meta.env.PROD && event.request?.url?.includes('localhost')) {
        console.log('⏭️ Skipping localhost error');
        return null;
      }
      return event;
    },
  });

  console.log('✅ Sentry initialized successfully!');
  Sentry.captureMessage('AboutBlank AI App Started', 'info');
} else {
  console.warn('⚠️ Sentry DSN not configured - error tracking disabled');
}

// Wrapper component to use hooks
function AppWrapper() {
  const { user } = useAppSelector((state) => state.auth);
  const userRole = getUserRole(user?.userType);

  return (
    <ThemeProvider defaultTheme={userRole.theme}>
      <TooltipProvider>
        <App />
      </TooltipProvider>
    </ThemeProvider>
  );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="max-w-md w-full space-y-4 text-center">
            <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
            <p className="text-muted-foreground">We're sorry for the inconvenience.</p>
            {import.meta.env.DEV && (
              <pre className="text-xs bg-muted p-3 rounded text-left overflow-auto max-h-40">
                {error.toString()}
              </pre>
            )}
            <button
              onClick={resetError}
              className="px-4 py-2 bg-theme-primary text-theme-primary-foreground rounded-md hover:bg-theme-primary/90"
            >
              Try Again
            </button>
          </div>
        </div>
      )}
      showDialog={import.meta.env.DEV}
    >
      <ErrorBoundary>
        <Provider store={store}>
          <AppWrapper />
        </Provider>
      </ErrorBoundary>
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
